
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AffiliateUsageLimitsService, AffiliateLimitsOverview, AffiliateDefaultLimits } from '@/services/AffiliateUsageLimitsService';
import { toast } from 'sonner';

export const useAffiliateUsageLimits = (affiliateId: string) => {
  const queryClient = useQueryClient();

  // OPTIMIZED: Get affiliate's limits overview with better caching
  const { data: limitsOverview, isLoading, refetch } = useQuery({
    queryKey: ['affiliateUsageLimits', affiliateId],
    queryFn: () => AffiliateUsageLimitsService.getAffiliateLimitsOverview(affiliateId),
    enabled: !!affiliateId,
    staleTime: 2 * 60 * 1000, // 2 minutes - data stays fresh longer
    gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache longer
    refetchInterval: 60 * 1000, // Reduced from 30s to 60s
    refetchOnWindowFocus: false, // Prevent refetch on window focus
    refetchOnReconnect: false, // Prevent refetch on reconnect
  });

  // OPTIMIZED: Get affiliate's default limits with caching
  const { data: affiliateDefaults } = useQuery({
    queryKey: ['affiliateDefaultLimits', affiliateId],
    queryFn: () => AffiliateUsageLimitsService.getAffiliateDefaultLimits(affiliateId),
    enabled: !!affiliateId,
    staleTime: 5 * 60 * 1000, // 5 minutes - defaults don't change often
    gcTime: 30 * 60 * 1000, // 30 minutes - keep in cache very long
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // Mutation to set affiliate default limits
  const setAffiliateDefaultsMutation = useMutation({
    mutationFn: ({ daily, monthly }: { daily: number; monthly: number }) => 
      AffiliateUsageLimitsService.setAffiliateDefaultLimits(affiliateId, daily, monthly),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['affiliateUsageLimits', affiliateId] });
      queryClient.invalidateQueries({ queryKey: ['affiliateDefaultLimits', affiliateId] });
      toast.success('Default limits updated successfully');
    },
    onError: (error) => {
      console.error('Error setting affiliate defaults:', error);
      toast.error('Failed to update default limits');
    }
  });

  // Mutation to apply defaults to all users
  const applyDefaultsToAllMutation = useMutation({
    mutationFn: ({ daily, monthly }: { daily: number; monthly: number }) => 
      AffiliateUsageLimitsService.applyDefaultsToAllUsers(affiliateId, daily, monthly),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['affiliateUsageLimits', affiliateId] });
      queryClient.invalidateQueries({ queryKey: ['affiliateDefaultLimits', affiliateId] });
      toast.success('Default limits applied to all users successfully');
    },
    onError: (error) => {
      console.error('Error applying defaults to all users:', error);
      toast.error('Failed to apply defaults to all users');
    }
  });

  // Mutation to set user limits (converts default to custom)
  const setUserLimitsMutation = useMutation({
    mutationFn: ({ userId, daily, monthly }: { 
      userId: string; 
      daily: number; 
      monthly: number; 
    }) => AffiliateUsageLimitsService.setUserLimits(affiliateId, userId, daily, monthly),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['affiliateUsageLimits', affiliateId] });
      toast.success('User limits updated successfully (converted to custom limits)');
    },
    onError: (error) => {
      console.error('Error setting user limits:', error);
      toast.error('Failed to update user limits');
    }
  });

  // Mutation to remove user limits (revert to default)
  const removeUserLimitsMutation = useMutation({
    mutationFn: (userId: string) => 
      AffiliateUsageLimitsService.removeUserLimits(affiliateId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['affiliateUsageLimits', affiliateId] });
      toast.success('User limits reset to default');
    },
    onError: (error) => {
      console.error('Error removing user limits:', error);
      toast.error('Failed to reset user limits');
    }
  });

  // Mutation to run backfill
  const backfillUsersMutation = useMutation({
    mutationFn: () => AffiliateUsageLimitsService.backfillAffiliateUsers(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['affiliateUsageLimits', affiliateId] });
      toast.success('Successfully backfilled user limits');
    },
    onError: (error) => {
      console.error('Error running backfill:', error);
      toast.error('Failed to backfill user limits');
    }
  });

  return {
    // Data
    limitsOverview,
    affiliateDefaults,
    totalLimits: limitsOverview?.totalLimits || { daily: 0, monthly: 0 },
    allocatedLimits: limitsOverview?.allocatedLimits || { daily: 0, monthly: 0 },
    availableToAllocate: limitsOverview?.availableToAllocate || { daily: 0, monthly: 0 },
    userAllocations: limitsOverview?.userAllocations || [],
    
    // Loading states
    isLoading,
    isUpdatingDefaults: setAffiliateDefaultsMutation.isPending,
    isApplyingDefaults: applyDefaultsToAllMutation.isPending,
    isUpdatingLimits: setUserLimitsMutation.isPending,
    isRemovingLimits: removeUserLimitsMutation.isPending,
    isBackfilling: backfillUsersMutation.isPending,
    
    // Actions
    setAffiliateDefaults: setAffiliateDefaultsMutation.mutate,
    applyDefaultsToAll: applyDefaultsToAllMutation.mutate,
    setUserLimits: setUserLimitsMutation.mutate,
    removeUserLimits: removeUserLimitsMutation.mutate,
    backfillUsers: backfillUsersMutation.mutate,
    refreshLimits: refetch,
    
    // Helper functions
    canAllocate: (daily: number, monthly: number) => {
      const available = limitsOverview?.availableToAllocate;
      return available ? 
        (daily <= available.daily && monthly <= available.monthly) : 
        false;
    },
    
    getAllocationPercentage: () => {
      const total = limitsOverview?.totalLimits;
      const allocated = limitsOverview?.allocatedLimits;
      if (!total || !allocated) return { daily: 0, monthly: 0 };
      
      return {
        daily: total.daily > 0 ? Math.round((allocated.daily / total.daily) * 100) : 0,
        monthly: total.monthly > 0 ? Math.round((allocated.monthly / total.monthly) * 100) : 0
      };
    }
  };
};

// OPTIMIZED: Hook for admin to manage all affiliate limits with better caching
export const useAdminAffiliateLimits = () => {
  const queryClient = useQueryClient();

  // Get all affiliates with their limits
  const { data: allAffiliateLimits, isLoading } = useQuery({
    queryKey: ['adminAffiliateLimits'],
    queryFn: () => AffiliateUsageLimitsService.getAllAffiliateLimits(),
    staleTime: 3 * 60 * 1000, // 3 minutes - admin data changes less frequently
    gcTime: 15 * 60 * 1000, // 15 minutes - keep in cache longer
    refetchInterval: 2 * 60 * 1000, // Reduced from 1 minute to 2 minutes
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // Mutation to set affiliate limits
  const setAffiliateLimitsMutation = useMutation({
    mutationFn: ({ 
      affiliateId, 
      daily, 
      monthly, 
      adminId 
    }: { 
      affiliateId: string; 
      daily: number; 
      monthly: number; 
      adminId: string; 
    }) => AffiliateUsageLimitsService.setAffiliateLimits(affiliateId, daily, monthly, adminId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminAffiliateLimits'] });
      queryClient.invalidateQueries({ queryKey: ['affiliateUsageLimits'] });
      toast.success('Affiliate limits updated successfully');
    },
    onError: (error) => {
      console.error('Error setting affiliate limits:', error);
      toast.error('Failed to update affiliate limits');
    }
  });

  return {
    // Data
    allAffiliateLimits: allAffiliateLimits || [],
    
    // Loading states
    isLoading,
    isUpdatingLimits: setAffiliateLimitsMutation.isPending,
    
    // Actions
    setAffiliateLimits: setAffiliateLimitsMutation.mutate,
    
    // Helper functions
    getTotalSystemUsage: () => {
      if (!allAffiliateLimits) return { daily: 0, monthly: 0 };
      
      return allAffiliateLimits.reduce(
        (total, affiliate) => ({
          daily: total.daily + affiliate.allocatedDaily,
          monthly: total.monthly + affiliate.allocatedMonthly
        }),
        { daily: 0, monthly: 0 }
      );
    }
  };
};
